import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import uuid4 from 'uuid4';

import { uiConfig } from '../../../../../../../conf/predicate';

// Components
import Form from '../../../../../../../../components/Form';
import { ScrollContainer } from '../../../../../../../../components/ScrollContainer';
import { DrawerContainer, DrawerContent } from '../../../../../../../../components/Drawer';
import motionProps from '../../../../../../../../components/Drawer/motion';
import FeedbackCard from '../../../../../../../../components/FeedbackCard';
import { PredicatePartsTable } from './PredicatePartsTable';
import { FormField } from '../../../../../../../../components/Form/components/FormField';

// Context
import { CriteriaStoreContext } from '../../../../../../state/criteria/context';
import { PredicateStoreContext } from '../../../../../../state/predicate/context';
import { RuleEditorContext } from '../context';

// Enums
import { dtoState } from '../../../../../../../../common/enums/dtoState';

// Hooks
import { useGetCriteriaPredicateMetadata } from '../../../../../../api/hooks/useGetCriteriaPredicateMetadata';
import { useSavePredicate } from '../../../../../../api/hooks/useSavePredicate';
import { useCreatePredicate } from '../../../../../../api/hooks/useCreatePredicate';
import { useCriteriaStorePredicate } from '../../../../../../state/criteria/hooks/useCriteriaStorePredicate';
import { useCriteriaStoreBuildDTO } from '../../../../../../state/criteria/hooks/useCriteriaStoreBuildDTO';
import { useFetchCriteriaResources } from '../../../../../../api/hooks/useFetchCriteriaResources';
import { usePredicateStorePredicate } from '../../../../../../state/predicate/hooks/usePredicateStorePredicate';
import { usePredicateStoreUpdatePredicatePart } from '../../../../../../state/predicate/hooks/usePredicateStoreUpdatePredicatePart';
import { usePredicateStoreAddPredicatePart } from '../../../../../../state/predicate/hooks/usePredicateStoreAddPredicatePart';
import { usePredicateStoreInit } from '../../../../../../state/predicate/hooks/usePredicateStoreInit';
import { usePredicateStoreReset } from '../../../../../../state/predicate/hooks/usePredicateStoreReset';
import { usePredicateStoreDeletePredicatePart } from '../../../../../../state/predicate/hooks/usePredicateStoreDeletePredicatePart';
import { updatePredicateStorePartOrder } from '../../../../../../state/predicate/hooks/updatePredicateStorePartOrder';
import { usePredicateStoreBuildDTO } from '../../../../../../state/predicate/hooks/usePredicateStoreBuildDTO';
import { useNextPredicateOrder } from '../../../../../../state/criteria/hooks/useNextPredicateOrder';

/// State Management
import { createPredicateStore } from '../../../../../../state/predicate/store';

export const SpinnerCont = styled.div`
    display: flex;
    justify-content: center;
`;

export const Container = styled.div`
    display: flex;
    flex-direction: column;
    max-height: 100vh;
`;

const DrawerRow = `
    padding: 1rem;
    display: flex;
    flex-direction: row;
`;

export const DrawerHeader = styled.div`
    ${DrawerRow};
    justify-content: space-between;
`;

export const DrawerFooter = styled.div`
    ${DrawerRow};
    justify-content: flex-end;
`;

const predicateStore = createPredicateStore({});

const PredicateEditorContextWrapper = ({ children, predicate }) => {
    const { sessionToken } = React.useContext(CriteriaStoreContext);
    const store = React.useRef(predicateStore).current;

    return (
        <PredicateStoreContext.Provider value={{ store, sessionToken }}>
            <PredicateStoreInit predicate={predicate}>{children}</PredicateStoreInit>
        </PredicateStoreContext.Provider>
    );
};

const PredicateStoreInit = ({ predicate, children }) => {
    const init = usePredicateStoreInit();

    React.useEffect(() => {
        init(predicate);
    }, [predicate]);

    return children;
};

const PredicateEditorBase = ({
    formModel,
    predicate,
    selectedPredicateId,
    closeDrawer,
    saveDisabled,
    onMount,
    parentPredicateUuid,
    onUpdate,
    isNew
}) => {
    const [createdPredicateUuid, setCreatedPredicateUuid] = React.useState(null);
    const isExisting = React.useMemo(() => createdPredicateUuid !== null || !isNew);
    const editingPredicateUuid = React.useMemo(() => createdPredicateUuid ?? selectedPredicateId, [
        createdPredicateUuid,
        selectedPredicateId
    ]);
    const { criteriaId } = React.useContext(RuleEditorContext);
    const { setEditing } = React.useContext(RuleEditorContext);
    const { sessionToken } = React.useContext(CriteriaStoreContext);
    const PredicateEditorRef = React.useRef(null);
    const predicateState = usePredicateStorePredicate(editingPredicateUuid);
    const [buttonClicked, setButtonClicked] = React.useState();
    const resetStore = usePredicateStoreReset();
    const updatePart = usePredicateStoreUpdatePredicatePart();
    const addPart = usePredicateStoreAddPredicatePart();
    const deletePart = usePredicateStoreDeletePredicatePart();
    const updatePartOrder = updatePredicateStorePartOrder();
    const buildDTO = usePredicateStoreBuildDTO();

    const mutationConfig = {
        sessionToken,
        criteriaId,
        onErrorCb: (e) => {
            setFatalError(e);
            // setWasSuccessful(false);
        },
        onSettledCb: () => {
            setButtonClicked();
        }
    };
    const updatePredicate = useSavePredicate({
        ...mutationConfig,
        predicateUuid: editingPredicateUuid,
        onSuccessCb: ({ context }) => {
            setEdited(false);
            if (context?.closeOnSuccess) {
                setTimeout(() => {
                    CloseDrawer();
                }, 500);
            }
        }
    });
    const addNewPredicate = useCreatePredicate({
        ...mutationConfig,
        parentPredicateUuid,
        onSuccessCb: ({ data, context }) => {
            setEdited(false);
            if (context?.closeOnSuccess) {
                setTimeout(() => {
                    CloseDrawer();
                }, 500);
            } else {
                setCreatedPredicateUuid(data?.uuid);
            }
        }
    });
    const [groupFn, setGroupFn] = React.useState('');
    const [groupFunctionOperator, setGroupFunctionOperator] = React.useState('');
    const [groupFunctionOperatorParam, setGroupFunctionOperatorParam] = React.useState('');
    const criteriaResourcesQuery = useFetchCriteriaResources({
        sessionToken
    });

    const CloseDrawer = () => {
        if (typeof closeDrawer === 'function') {
            closeDrawer();
        }
        resetStore();
    };

    const criteriaPredicateMetadataQuery = useGetCriteriaPredicateMetadata({ sessionToken });
    const [compose, updateCompose] = React.useState(false);
    const [edited, setEdited] = React.useState(false);
    const [errors, setErrors] = React.useState({});
    const [fatalError, setFatalError] = React.useState(null);

    const prepareDocument = (docState, newPredicate) => {
        const updatedDocState = { ...docState };

        updatedDocState.groupFunctionDTO = groupFn
            ? sortedResources?.Function?.find(({ resourceId }) => resourceId === groupFn)
            : undefined;
        updatedDocState.groupFunctionOperatorDTO = groupFunctionOperator
            ? sortedResources?.Operator?.find(({ resourceId }) => resourceId === groupFunctionOperator)
            : undefined;
        updatedDocState.groupFunctionOperatorParamDTO =
            updatedDocState.groupFunctionOperatorDTO && groupFunctionOperatorParam
                ? updatedDocState.groupFunctionOperatorDTO?.criteriaResourceParams?.find(
                      ({ paramId }) => paramId === groupFunctionOperatorParam
                  )
                : undefined;

        return {
            ...updatedDocState,
            dtoState: newPredicate || updatedDocState.dtoState === dtoState.New ? dtoState.New : dtoState.Updated
        };
    };

    const onApplyFormState = (formState) => {
        if (saveDisabled) {
            console.warn('Authoring Interface is currently read-only.');
            return;
        }
        let update = prepareDocument(
            {
                ...predicateState,
                ...formState
            },
            !isExisting
        );
        updateCompose(false);
        let { valid } = validate(update);
        if (!valid) return;
        // if (isExistingDoc(update)) {
        //     setFatalError('This predicate already exists in the system.');
        //     return;
        // }
        const payload = buildDTO(update);

        if (!isExisting) {
            addNewPredicate.mutate({
                newPredicate: payload,
                parentPredicateUuid: selectedPredicateId,
                context: { closeOnSuccess: buttonClicked === 'save' }
            });
        } else {
            updatePredicate.mutate({ update: payload, context: { closeOnSuccess: buttonClicked === 'save' } });
        }
    };

    const isLoading = React.useMemo(
        () => criteriaResourcesQuery.isLoading || criteriaPredicateMetadataQuery.isLoading || updatePredicate.isLoading,
        [criteriaPredicateMetadataQuery, criteriaResourcesQuery, updatePredicate]
    );

    // Temporary validation solution
    const validate = (formState) => {
        let errors = {};
        for (let field of formModel) {
            let { valid, failed } = field.validation(formState[field.accessor]);
            if (!valid) {
                errors[field.accessor] = 'Invalid Input: ' + failed.join(', ');
            }
            // Add additional validation here;
        }
        // Add relational validation here

        setErrors(errors);
        let valid = Object.keys(errors).length === 0;
        return { valid, failureMsg: '' };
    };
    const sortedResources = React.useMemo(() => {
        if (criteriaResourcesQuery?.data?.length) {
            return criteriaResourcesQuery?.data.reduce((acc, curr) => {
                if (typeof acc?.[curr.resourceType] === 'undefined') {
                    acc[curr.resourceType] = [];
                }

                acc[curr.resourceType].push(curr);

                return acc;
            }, {});
        }

        return {};
    }, [criteriaResourcesQuery?.data]);

    const reduceResourcesToMap = (resources = [], key = '') => {
        return resources.reduce((acc, data) => {
            return {
                ...acc,
                [data?.[key]]: data?.name
            };
        }, {});
    };

    const optionsOverride = React.useMemo(() => {
        let optionsOverride = {};
        optionsOverride.predicateType = Object.entries(
            criteriaPredicateMetadataQuery?.data?.predicateType ?? {}
        ).reduce(
            (acc, [key, value]) => [
                ...acc,
                {
                    label: key,
                    value,
                    disabled: key === 'Criteria'
                }
            ],
            []
        );
        optionsOverride.predicateConjunction = criteriaPredicateMetadataQuery?.data?.predicateConjunction;
        optionsOverride.groupFunctionDTO = reduceResourcesToMap(sortedResources?.Function, 'resourceId');
        optionsOverride.groupFunctionOperatorDTO = reduceResourcesToMap(sortedResources?.Operator, 'resourceId');
        optionsOverride.groupFunctionOperatorParamDTO = groupFunctionOperator
            ? reduceResourcesToMap(
                  sortedResources?.Operator?.find(({ resourceId }) => resourceId === groupFunctionOperator)
                      ?.criteriaResourceParams,
                  'paramId'
              )
            : {};

        return optionsOverride;
    }, [criteriaPredicateMetadataQuery?.data, sortedResources, groupFunctionOperator]);

    for (let key in optionsOverride) {
        let subIndex = formModel.findIndex((v) => v.accessor === key);
        if (subIndex !== -1) {
            formModel[subIndex].options = optionsOverride[key];
        }
    }
    const onCloseDrawer = React.useCallback(() => {
        if (edited) {
            PredicateEditorRef?.current?.setShowDiscardChangesModal(true);
        } else {
            CloseDrawer();
        }
    }, [PredicateEditorRef, CloseDrawer, edited]);

    return (
        <DrawerContainer
            open={editingPredicateUuid != null}
            width="75vw"
            push={{ distance: 64 }}
            onClose={() => {
                onCloseDrawer();
            }}
            placement="right"
            rootClassName="level-0"
            className="drawer1"
            zIndex={9999}
            onKeyDown={(e) => {
                if (e?.keyCode === 27) onCloseDrawer();
            }}
            {...motionProps}
        >
            <DrawerContent
                ref={PredicateEditorRef}
                open={editingPredicateUuid != null}
                title={`${!isExisting ? 'Add' : 'Edit'} Predicate`}
                saveDisabled={!edited || saveDisabled}
                onSave={() => {
                    setButtonClicked('save');
                    updateCompose(true);
                }}
                onApply={() => {
                    setButtonClicked('apply');
                    updateCompose(true);
                }}
                closeFn={onCloseDrawer}
                closeDrawer={CloseDrawer}
                isLoading={isLoading}
            >
                {editingPredicateUuid != null ? (
                    <ScrollContainer style={{ marginTop: 0 }}>
                        <div style={{ padding: '1rem' }}>
                            {fatalError != null && (
                                <FeedbackCard
                                    intent={'danger'}
                                    feedback={{
                                        heading: `An error has occurred.`,
                                        message:
                                            typeof fatalError === 'string'
                                                ? fatalError
                                                : `Please wait a few moments before trying your request
    again.`
                                    }}
                                    details={fatalError}
                                />
                            )}
                            {!isExisting ? 'Enter' : 'Adjust'} required information below.
                            <p style={{ margin: `1.33rem 0 0 0` }}></p>
                            <Form
                                formModel={formModel}
                                data={predicateState}
                                errors={errors}
                                compose={compose}
                                onApplyFormState={onApplyFormState}
                                setEdited={setEdited}
                                fatalError={fatalError}
                                onMount={onMount}
                                menuPortalDocument
                                compact
                                hookFxns={{
                                    updateGroupFn: setGroupFn,
                                    updateGroupFnOperator: setGroupFunctionOperator,
                                    updateGroupFnOperatorParam: setGroupFunctionOperatorParam
                                }}
                            />
                            <FormField
                                style={{ marginTop: '2rem' }}
                                label={'Criteria Predicate Part Entries'}
                                labelFor={'predicate-parts-table'}
                                readOnly={true}
                                compact={true}
                                size="sm"
                                // error={errors?.valueSetDTO}
                            >
                                <PredicatePartsTable
                                    setEdited={setEdited}
                                    parentPredicateUuid={editingPredicateUuid}
                                    predicatePartUuids={predicateState?.predicateParts ?? []}
                                    parentDrawerRef={PredicateEditorRef}
                                    onApply={(predicatePart, isNewPart) => {
                                        setEdited(true);

                                        if (isNewPart) {
                                            addPart({ predicatePart, parentUuid: editingPredicateUuid });
                                        } else {
                                            updatePart({ predicatePart });
                                        }
                                    }}
                                    onChangeSortOrder={(newOrderedPredicatePartUuids = []) => {
                                        setEdited(true);
                                        updatePartOrder({
                                            newOrderedPredicatePartUuids,
                                            parentUuid: editingPredicateUuid
                                        });
                                    }}
                                    onDelete={(part) => {
                                        setEdited(true);
                                        deletePart({ predicatePart: part, parentUuid: editingPredicateUuid });
                                    }}
                                />
                            </FormField>
                        </div>
                    </ScrollContainer>
                ) : null}
            </DrawerContent>
        </DrawerContainer>
    );
};

const ExistingPredicateEditor = (props) => {
    const targetPredicate = useCriteriaStorePredicate(props.selectedPredicateId);
    const buildDTO = useCriteriaStoreBuildDTO();
    const predicate = buildDTO(targetPredicate);

    return (
        <PredicateEditorContextWrapper predicate={predicate}>
            <PredicateEditorBase {...props} predicate={predicate} />
        </PredicateEditorContextWrapper>
    );
};

const NewPredicateEditor = (props) => {
    const { criteriaId } = React.useContext(RuleEditorContext);
    const parentPredicateUuid = props.selectedPredicateId?.split?.('_')[1] ?? null;
    const parent = useCriteriaStorePredicate(parentPredicateUuid, ['predicates']);
    const newPredicateOrder = useNextPredicateOrder({
        parentUuid: parentPredicateUuid
    });

    const predicate = {
        ...uiConfig.newDoc,
        ...(parent ? { parentPredicateId: parent.predicateId } : {}),
        criteriaId,
        predicateOrder: newPredicateOrder,
        // predicateId: `TEMP_${uuid4()}`, // Remove this and force save/apply before adding parts
        uuid: uuid4()
    };

    return (
        <PredicateEditorContextWrapper predicate={predicate}>
            <PredicateEditorBase
                {...props}
                isNew
                parentPredicateUuid={parentPredicateUuid}
                selectedPredicateId={predicate.uuid}
            />
        </PredicateEditorContextWrapper>
    );
};

export const PredicateEditor = (props) => {
    const isNew = props?.selectedPredicateId?.startsWith?.('new') ?? false;

    return isNew ? <NewPredicateEditor {...props} /> : <ExistingPredicateEditor {...props} />;
};

export default PredicateEditor;

const SharedPredicateEditorProps = {
    selectedPredicateId: PropTypes.string,
    formModel: PropTypes.arrayOf(PropTypes.object),
    closeDrawer: PropTypes.func,
    onUpdate: PropTypes.func,
    onMount: PropTypes.func,
    saveDisabled: PropTypes.bool
};

PredicateEditor.propTypes = { ...SharedPredicateEditorProps };
ExistingPredicateEditor.propTypes = { ...SharedPredicateEditorProps };
NewPredicateEditor.propTypes = { ...SharedPredicateEditorProps };
PredicateEditorBase.propTypes = { ...SharedPredicateEditorProps, predicate: PropTypes.object };
